import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import EyeIcon from "../../assets/image/eye-icon.svg";
import EditIcon from "../../assets/image/edit-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/driver-management";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const mapDriverRow = (d) => {
  const statusNum = d.status === 1 || d.status === "1" ? 1 : 0;
  return {
    id: d._id,
    driverId:
      d.driver_ref_id != null && d.driver_ref_id !== ""
        ? `${d.driver_ref_id}`
        : "—",
    name: d.full_name ?? "—",
    mobile: [d.country_code, d.mobile_number].filter(Boolean).join(" ") || "—",
    licence: d.license_number ?? "—",
    status: statusNum,
    licenseStatusLabel: d.license_expire ? "Expired" : "Active",
    statusLabel: statusNum === 1 ? "Active" : "Inactive",
    raw: d,
  };
};

const DriverManagement = () => {
  const [searchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [deleteId, setDeleteId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const applyRestoredState = useCallback((saved) => {
    if (saved.search != null) setSearch(saved.search);
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    if (saved.limit) {
      const l = Number(saved.limit);
      if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
    }
  }, []);

  const { detailListQuery } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      limit: itemsPerPage,
      search: search.trim() || undefined,
    },
    applyRestoredState
  );

  useEffect(() => {
    let cancelled = false;

    const loadDrivers = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        notifyError("Session expired. Please log in again.");
        setDrivers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.listOfDriver}?page=${currentPage}&limit=${itemsPerPage}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          notifyError(getErrorMessage(payload, response));
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          notifyError(getErrorMessage(payload, response));
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.driver_data) ? data.driver_data : [];

        setDrivers(list.map(mapDriverRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          notifyError(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load drivers."
          );
          setDrivers([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDrivers();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage]);

  const filteredDrivers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter(
      (driver) =>
        String(driver.name).toLowerCase().includes(q) ||
        String(driver.driverId).toLowerCase().includes(q) ||
        String(driver.licence).toLowerCase().includes(q) ||
        String(driver.mobile).toLowerCase().includes(q)
    );
  }, [drivers, search]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const totalPages = apiTotalPages;
  const serialBase = search.trim() ? 0 : indexOfFirst;

  const handleDelete = () => {
    setDrivers((prev) => prev.filter((d) => d.id !== deleteId));
    setDeleteId(null);
  };

  const updateDriverStatus = async (driver, nextStatus) => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      notifyError("Session expired. Please log in again.");
      return;
    }

    setUpdatingStatusId(driver.id);

    try {
      const response = await fetch(
        `${ACCOUNTANT_API}${endpoint.updateStatusDriver}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            driver_id: driver.id,
            status: nextStatus,
          }),
        }
      );

      const payload = await parseBody(response);

      if (!response.ok) {
        notifyError(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        notifyError(getErrorMessage(payload, response));
        return;
      }

      setDrivers((prev) =>
        prev.map((d) =>
          d.id === driver.id
            ? {
                ...d,
                status: nextStatus,
                statusLabel: nextStatus === 1 ? "Active" : "Inactive",
                raw: { ...d.raw, status: nextStatus },
              }
            : d
        )
      );

      notifySuccess(payload?.message || "Status updated.");
    } catch (err) {
      console.error(err);
      notifyError(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not update status."
      );
    } finally {
      setUpdatingStatusId(null);
    }
  };

  return (
    <>
      <div className="tab-pane-content mt-7">
        <div className="flex gap-x-2.5 mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearch(normalizeSearchInput(e.target.value))}
          />

          <Link className="btn btn-danger ml-auto" to="/driver-management/add-new">
            Add New Driver
          </Link>
        </div>

        <div className="common-table-wrapper m-0">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Driver ID</th>
                  <th>Driver Name</th>
                  <th>Mobile Number</th>
                  <th>Licence Number</th>
                  <th>Licence Status</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      Loading drivers…
                    </td>
                  </tr>
                ) : filteredDrivers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No drivers found.
                    </td>
                  </tr>
                ) : (
                  filteredDrivers.map((driver, index) => (
                    <tr key={driver.id}>
                      <td>{serialBase + index + 1}</td>
                      <td>{driver.driverId}</td>
                      <td>{driver.name}</td>
                      <td>{driver.mobile}</td>
                      <td>{driver.licence}</td>
                      <td>{driver.licenseStatusLabel}</td>
                      <td>{driver.statusLabel}</td>

                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <label
                            className={`relative inline-flex items-center min-w-9 h-5 rounded-full my-0 transition-all ${
                              driver.status === 1
                                ? "bg-[#17B517] [&_.toggle-knob]:!left-[17px]"
                                : "bg-[#E11818]"
                            } ${updatingStatusId === driver.id ? "opacity-60 pointer-events-none" : "cursor-pointer"}`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={driver.status === 1}
                              disabled={loading || updatingStatusId === driver.id}
                              onChange={() => {
                                const next = driver.status === 1 ? 0 : 1;
                                updateDriverStatus(driver, next);
                              }}
                              aria-label={
                                driver.status === 1
                                  ? "Set driver inactive"
                                  : "Set driver active"
                              }
                            />
                            <div className="toggle-knob absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all" />
                          </label>
                          <Link
                            to={`/driver-management/edit-driver-details?driver_id=${encodeURIComponent(
                              driver.id
                            )}${detailListQuery}`}
                          >
                            <img src={EditIcon} className="h-7 min-w-[22px]" alt="Edit" />
                          </Link>
                          <Link
                            to={`/driver-management/view-detail?driver_id=${encodeURIComponent(
                              driver.id
                            )}${detailListQuery}`}
                          >
                            <img src={EyeIcon} className="h-7 min-w-[22px]" alt="View" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-x-4 items-center mt-4">
          <select
            className="p-2 shadow-sm outline-none"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>

          <div className="text-sm text-gray-500 ml-auto">
            {totalRecords} records found
          </div>

          <div className="flex items-center gap-2 pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === 1 ? "" : "cursor-pointer"
              }`}
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || loading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === totalPages ? "" : "cursor-pointer"
              }`}
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>

      <Toastify />

      {deleteId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-xl">
            <p className="mb-4 text-center font-bold">
              Are you sure you want to delete?
            </p>

            <div className="flex justify-center gap-4">
              <button
                type="button"
                className="border px-4 py-2 rounded"
                onClick={() => setDeleteId(null)}
              >
                No
              </button>

              <button
                type="button"
                className="bg-red-500 text-white px-4 py-2 rounded"
                onClick={handleDelete}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverManagement;
